import { getUserAdapter } from "../gtm/GtmUtils";
import { DataSyncLibEventNames } from "../shared/DataSyncLibEventNames";
import { Logger } from "../utils/Logger";
import { PropertyValuePair } from "./PropertyValuePair";
import { CmpService } from "../cmp/CmpService";
import { container } from "tsyringe";

/**
 * Register a function to invoke once the CDP's profile has been loaded and setup.
 * @param callback The function to invoke
 */
export function invokeWhenProfileSetup(callback: () => void): void {
  if (cdpProfileIsSetup()) {
    callback();
  } else {
    window.addEventListener(DataSyncLibEventNames.CdpProfileSetup, callback);
  }
}

/**
 * Register a function to invoke once the consent change has been handled by the library.
 * @param callback The function to invoke
 */
export function onConsentChangeComplete(callback: () => void): void {
  const cmpService = container.resolve(CmpService);
  if (cmpService.ConsentHandled) {
    callback();
  } else {
    window.addEventListener(DataSyncLibEventNames.ConsentChangeHandled, callback, { once: true });
  }
}

/**
 * Register a function to invoke once the CDP's profile has been loaded.
 * @remarks
 * Warning: function will be invoked BEFORE the profile is set up. Unless your callback doesn't involve using the profile
 * of the current user, you probably want to use invokeWhenProfileSetup instead.
 * @param callback The function to invoke
 * @see invokeWhenProfileSetup
 */
export function invokeWhenProfileLoaded(callback: () => void): void {
  // This condition comes directly from BlueConic's documentation.
  // See https://support.blueconic.com/hc/en-us/articles/202605221-JavaScript-front-end-API
  // Is BlueConic loaded?
  if (
    typeof blueConicClient !== "undefined" &&
    typeof blueConicClient.event !== "undefined" &&
    typeof blueConicClient.event.subscribe !== "undefined"
  ) {
    // BlueConic is loaded, now we can do API things.
    // Is the profile loaded and set up?
    if (cdpProfileIsSetup()) {
      callback();
    } else {
      runWhenProfileReady(callback);
    }
  } else {
    window.addEventListener(
      "onBlueConicLoaded",
      () => {
        runWhenProfileReady(callback);
      },
      false,
    );
  }

  function runWhenProfileReady(toInvoke: () => void): void {
    // We are using onEventReady here instead of onReady because it sometimes creates
    // profile conflicts with the listeners when we need to create a new profile on page load.
    blueConicClient.event.subscribe(blueConicClient.event.onEventReady, {}, (_, context) => {
      // We want to run this code only once.
      if (context.isFirstLoad) {
        toInvoke();
      }
    });
  }
}

/**
 * Check if the CDP profile has been loaded and set up based on the current user
 * in the Data Layer.
 */
function cdpProfileIsSetup(): boolean {
  const user = getUserAdapter();
  return !!user && !!user.cdpProfileId;
}

/**
 * Check the local storage to validate and compare it to the session length to check if a new web session should start
 * @param sessionLengthInMinutes The session length in minutes
 * @return Returns true if the session is expired and the browser can read/write in the localStorage
 */
export function shouldStartNewWebSession(sessionLengthInMinutes: number): boolean {
  const startWebSessionKey = "gtm_startWebSessionKey";

  const storedStartWebSessionValue = localStorage.getItem(startWebSessionKey);
  const now = new Date();
  if (!storedStartWebSessionValue) {
    Logger.verbose("No stored web session. Creating a new session");
    localStorage.setItem(startWebSessionKey, now.toISOString());
    return !!localStorage.getItem(startWebSessionKey);
  }

  const parsedStartWebSessionValue = Date.parse(storedStartWebSessionValue);
  if (isNaN(parsedStartWebSessionValue)) {
    Logger.verbose("Stored web session expiration is not a valid date. Creating a new session");
    localStorage.setItem(startWebSessionKey, now.toISOString());
    return !!localStorage.getItem(startWebSessionKey);
  }

  const expirationDate = new Date(parsedStartWebSessionValue).getTime() + sessionLengthInMinutes * 60000; // 60000 = a minute in milliseconds
  if (now.getTime() > expirationDate) {
    Logger.verbose("Stored web session has expired. Creating a new session");
    localStorage.setItem(startWebSessionKey, now.toISOString());
    return !!localStorage.getItem(startWebSessionKey);
  }

  Logger.verbose("Stored web session isn't expired"); // Used in tests!
  return false;
}

/**
 * Set the value of a list of properties and log a verbose message for each
 * after the update is complete.
 * @param pairs A list of property-value pairs to update
 */
export function updateProfileProperties(pairs: PropertyValuePair[]): void {
  const profile = blueConicClient.profile.getProfile();
  for (const pair of pairs) {
    profile.setValue(pair.propertyId, pair.value);
  }
  blueConicClient.profile.updateProfile(undefined, () => {
    for (const pair of pairs) {
      Logger.verbose(`CDP: Profile property "${pair.propertyId}" updated to ${pair.value}`);
    }
  });
}
